import React, { useEffect, useRef, useState } from 'react';

import { Modal } from '@wds/modal';
import { Box } from '@wds/box';
import { Button } from '@wds/button';
import { Input } from '@wds/input';
import { Typography } from '@wds/typography';

import { getCookie } from 'sma.common/utils';

import PropTypes from 'prop-types';
import STRINGS from '../../constants';

const DeactivateMFAModal = (props) => {
	const {
		open,
		setOpen,
		handleOnSuccess,
		method,
	} = props;
	const [challengeCode, setChallengeCode] = useState('');
	const [error, setError] = useState('');
	const [isValid, setIsValid] = useState(false);
	const [isLoading, setIsLoading] = useState(false);
	const [sending, setSending] = useState(false);
	const [cooldown, setCooldown] = useState(0);
	const [message, setMessage] = useState('');
	const sentOnceRef = React.useRef(false);
	const timerRef = useRef(null);

	const RESEND_COOLDOWN_SEC = 120;

	useEffect(() => {
		if (challengeCode.match(/^[0-9]+$/) && challengeCode.length === 6) {
			setIsValid(true);
		} else {
			setIsValid(false);
		}
	}, [challengeCode]);

	useEffect(() => {
		if (timerRef.current) {
			clearInterval(timerRef.current);
		}
	}, []);

	const removeMFA = async () => {
		try {
			setIsLoading(true);
			const response = await fetch(reverse('api-internal-mfa-remove'), {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
					'X-CSRFToken': getCookie('csrftoken'),
				},
				body: JSON.stringify({ code: challengeCode, action: 'remove_mfa', method }),
			});
			return await response.json();
		} catch (err) {
			console.log(err);
			setError(STRINGS.unknownError);
		} finally {
			setIsLoading(false);
		}
	};

	const handleOnClose = () => {
		setOpen(false);
	};

	const handleErrorResponse = async (response) => {
		const errorData = await response.json();
		if (response.status === 401 || response.status === 400 || response.status === 403) {
			setError(errorData.error);
		} else {
			setError('unknownError');
		}
	};

	const handleError = (msg) => {
		setError(msg);
	};

	const handleDeactivateMFA = () => {
		removeMFA()
			.then((data) => {
				if (data && data.error) {
					if (data.error === 'invalid_code_locked_out' || data.error === 'not_authenticated') {
						window.location.href = reverse('mfa-lockout');
						return;
					}
					setError(data.error);
				} else {
					setError('');
					setChallengeCode('');
					handleOnSuccess();
				}
			})
			.catch((err) => {
				setError(err.message || STRINGS.unknownError);
			});
	};

	const handleOnCancel = () => {
		setChallengeCode('');
		handleOnClose();
	};

	const startCooldown = (remaining) => {
		if (remaining) {
			setCooldown(remaining);
		} else {
			setCooldown(RESEND_COOLDOWN_SEC);
		}
		if (timerRef.current) clearInterval(timerRef.current);
		timerRef.current = setInterval(() => {
			setCooldown((s) => {
				if (s <= 1) {
					clearInterval(timerRef.current);
					return 0;
				}
				return s - 1;
			});
		}, 1000);
	};

	const sendEmailCode = async () => {
		if (sending || cooldown > 0) return;
		setSending(true);
		setError('');
		setMessage('');
		try {
			const resp = await fetch(reverse('api-internal-mfa-challenge'), {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
					'X-CSRFToken': getCookie('csrftoken'),
				},
				body: JSON.stringify({ action: 'send_code', method: 'email' }),
				credentials: 'same-origin',
			});
			const data = await resp.json();
			if (data.success) {
				setMessage(STRINGS.code_sent);
				sentOnceRef.current = true;
				startCooldown();
			} else if (data.message) {
				setMessage(STRINGS?.[data.message]);
			} else if (data.error) {
				if (data.error === 'invalid_code_locked_out' || data.error === 'not_authenticated') {
					window.location.href = reverse('mfa-lockout');
				} else if (data.error === 'code_already_pending') {
					setError(data.error);
					startCooldown(data.time_remaining);
				}
			} else {
				setMessage(STRINGS.unknownError);
			}
		} catch (err) {
			handleError(`${STRINGS.errorHasOccurred}: ${err}`);
		} finally {
			setSending(false);
		}
	};

	const inputColor = error ? 'error' : '';

	return (
		<Box id='sma-deactivate-mfa-modal'>
			<Modal
				show={open}
				data-testid='mfa-deactivate-mfa-modal'
				onCancel={handleOnCancel}
				onClose={handleOnClose}
			>
				<Modal.Header header={STRINGS.deactivateMFATitle} />
				<Modal.Body>
					<Box display='flex' gap={2} mb={2} flexDirection='column'>
						<Typography component='p'>
							{STRINGS.deactivateMFAWarning}
							{' '}
							<Typography
								component='a'
								variant='link'
								href='https://help.surveymonkey.com/en/apply/faqs/#faq'
								target='_blank'
								rel='noopener noreferrer'
							>
								{STRINGS.deactivateMFAWarningLink}
							</Typography>
							{' '}
							{STRINGS.deactivateMFAWarning2}
						</Typography>
						<Typography component='p'>
							{method === 'totp' ? STRINGS.deactivateMFAContinueTOTP : STRINGS.deactivateMFAContinueEmail}
							{' '}
							<Typography
								component='a'
								variant='link'
								href='https://help.surveymonkey.com/en/apply/faqs/#faq'
								target='_blank'
								rel='noopener noreferrer'
							>
								{STRINGS.tryAnotherMethod}
							</Typography>
						</Typography>
						{message && (
							<Box
								p={4}
								style={{
									backgroundColor: '#42ac14',
									borderRadius: '5px',
									color: '#FFFFFF',
								}}
								role='alert'
								aria-live='assertive'
							>
								{STRINGS?.[message] || message}
							</Box>
						)}
						{error && (
							<Box
								p={4}
								style={{
									backgroundColor: '#AC145A',
									borderRadius: '5px',
									color: '#FFFFFF',
								}}
								role='alert'
								aria-live='assertive'
							>
								{STRINGS?.[error] || error}
							</Box>
						)}
						{method === 'email' && (
							<Button
								onClick={() => sendEmailCode()}
								id='start-resend-email-button'
								size='lg'
								aria-label={STRINGS.sendCode}
								disabled={isLoading || cooldown > 0}
							>
								{cooldown > 0 ? `${STRINGS.sendCode} (${cooldown})` : (STRINGS.sendCode)}
							</Button>
						)}
						<Input
							value={challengeCode}
							placeholder={STRINGS.enterCodeProvided}
							onChange={(e) => setChallengeCode(e.target.value)}
							id='mfa-challenge-code'
							type='text'
							color={inputColor}
							stretched
							aria-required='true'
							errorMessage={error ? (STRINGS?.[error] || error || STRINGS.unknownError) : undefined}
							autoFocus
						/>
					</Box>
				</Modal.Body>
				<Modal.Footer align='right'>
					<Button
						onClick={handleOnClose}
						variant='text'
						color='secondary'
						id='sma-deactviate-mfa-modal-cancel-button'
						aria-label={STRINGS.cancelBtn}
					>
						{STRINGS.cancelBtn}
					</Button>
					<Button
						onClick={() => handleDeactivateMFA()}
						id='sma-deactviate-mfa-button'
						color='warning'
						aria-label={STRINGS.deactivateMFA}
						disabled={!isValid || isLoading}
						aria-disabled={!isValid}
					>
						{STRINGS.deactivateMFA}
					</Button>
				</Modal.Footer>
			</Modal>
		</Box>
	);
};

DeactivateMFAModal.propTypes = {
	open: PropTypes.bool.isRequired,
	setOpen: PropTypes.func.isRequired,
	handleOnSuccess: PropTypes.func.isRequired,
	method: PropTypes.string.isRequired,
};

export default DeactivateMFAModal;
